import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, Send, Sparkles, ChevronDown, ShoppingBag, Check, X } from 'lucide-react';
import { useSelectedStore } from '@/store/dataStore';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'bigtime_lidia_session';
const SEEN_KEY = 'bigtime_lidia_seen';

// Fallback defaults if /lidia/config-public is slow/unreachable.
const DEFAULT_GREETING =
  '¡Hola! Soy **Lidia** de BIGTIME 🙂. Puedo orientarte con productos, envíos, pagos, garantías y la tarjeta de sellos.\n\n¿En qué te ayudo hoy?';
const DEFAULT_CHIPS = [
  '¿Precios al mayor o al detal?',
  '¿Cómo funciona la tarjeta de sellos?',
  '¿Qué medios de pago aceptan?',
  '¿Envían a mi ciudad?',
];

// Trigger phrase — must match the prompt directive in server.py _build_lidia_prompt().
const ORDER_TRIGGER = /^\s*confirmar\s+pedido\b/i;

function formatBubble(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <b key={i}>{p.slice(2, -2)}</b>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// ---- Inline order form shown when the user types "CONFIRMAR PEDIDO" -----------
function LidiaOrderForm({ storeId, onCancel, onSubmit, busy }) {
  const [f, setF] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('bigtime_customer_data') || '{}');
      return {
        name: saved.name || '', cedula: saved.cedula || '', phone: saved.phone || '',
        city: saved.city || '', address: saved.address || '', items: '', note: '',
      };
    } catch { return { name: '', cedula: '', phone: '', city: '', address: '', items: '', note: '' }; }
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.cedula.trim() || !f.phone.trim() || !f.items.trim()) return;
    onSubmit(f);
  };
  return (
    <form onSubmit={submit} className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-3 space-y-2" data-testid="lidia-order-form">
      <div className="flex items-center gap-2 text-[#1B2A4A] font-bold text-sm">
        <ShoppingBag size={16} /> Confirmar pedido en <b>Pago Pendiente</b>
      </div>
      <p className="text-[11px] text-slate-600 leading-snug">
        Llena estos datos y la tienda te contactará por WhatsApp para cerrar. La tienda ya está seleccionada: <b>{storeId || 'central'}</b>.
      </p>
      <input required value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Nombre y apellido" className="w-full text-xs px-2.5 py-1.5 rounded-lg border" data-testid="lidia-of-name" />
      <div className="grid grid-cols-2 gap-2">
        <input required value={f.cedula} onChange={(e) => set('cedula', e.target.value)} placeholder="Cédula (V-...)" className="text-xs px-2.5 py-1.5 rounded-lg border" data-testid="lidia-of-cedula" />
        <input required value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Teléfono" className="text-xs px-2.5 py-1.5 rounded-lg border" data-testid="lidia-of-phone" />
      </div>
      <input value={f.city} onChange={(e) => set('city', e.target.value)} placeholder="Ciudad" className="w-full text-xs px-2.5 py-1.5 rounded-lg border" />
      <input value={f.address} onChange={(e) => set('address', e.target.value)} placeholder="Dirección de entrega" className="w-full text-xs px-2.5 py-1.5 rounded-lg border" />
      <textarea required rows={2} value={f.items} onChange={(e) => set('items', e.target.value)}
        placeholder="Pega el listado que armaste con Lidia: 1 nombre × cantidad por línea"
        className="w-full text-xs px-2.5 py-1.5 rounded-lg border resize-none"
        data-testid="lidia-of-items"
      />
      <textarea rows={1} value={f.note} onChange={(e) => set('note', e.target.value)} placeholder="Nota (opcional)" className="w-full text-xs px-2.5 py-1.5 rounded-lg border resize-none" />
      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={busy} className="flex-1 flex items-center justify-center gap-1 bg-[#1B2A4A] text-yellow-300 disabled:opacity-40 rounded-full py-2 text-xs font-bold" data-testid="lidia-of-submit">
          <Check size={13} /> Enviar
        </button>
        <button type="button" onClick={onCancel} className="px-3 bg-white border rounded-full text-xs text-slate-600" data-testid="lidia-of-cancel">
          <X size={13} />
        </button>
      </div>
    </form>
  );
}

export default function LidiaChatWidget() {
  const { store } = useSelectedStore();
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null); // null until loaded
  const [messages, setMessages] = useState([{ role: 'assistant', content: DEFAULT_GREETING }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [pulse, setPulse] = useState(true);
  const [orderPanel, setOrderPanel] = useState(null); // { itemsText, wa_link?, code? } | null
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; try { abortRef.current?.abort(); } catch { /* noop */ } };
  }, []);

  // Load admin-editable public config once.
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/lidia/config-public`).then((r) => r.json()).then((cfg) => {
      if (!alive) return;
      setConfig(cfg);
      setMessages([{ role: 'assistant', content: cfg.greeting || DEFAULT_GREETING }]);
    }).catch(() => setConfig({ active: true, assistant_name: 'Lidia', greeting: DEFAULT_GREETING, quick_chips: DEFAULT_CHIPS }));
    return () => { alive = false; };
  }, []);

  useEffect(() => { if (localStorage.getItem(SEEN_KEY)) setPulse(false); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, open, busy, orderPanel]);

  const openChat = () => { setOpen(true); setPulse(false); localStorage.setItem(SEEN_KEY, '1'); };

  const send = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || busy) return;
    setInput('');

    // Intercept order-trigger locally BEFORE hitting the LLM.
    if (ORDER_TRIGGER.test(text)) {
      setMessages((m) => [...m, { role: 'user', content: text }]);
      setOrderPanel({});
      return;
    }

    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '', pending: true }]);
    setBusy(true);
    const safeSet = (updater) => { if (mountedRef.current) setMessages(updater); };
    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await fetch(`${API_BASE}/chat/lidia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          message: text,
          session_id: localStorage.getItem(SESSION_KEY) || undefined,
          customer_name: (() => {
            try { return (JSON.parse(localStorage.getItem('bigtime_customer_data') || '{}') || {}).name || null; } catch { return null; }
          })(),
        }),
      });
      if (!res.ok || !res.body) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j.detail) detail = j.detail; } catch { /* noop */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!mountedRef.current) { try { reader.cancel(); } catch { /* noop */ } break; }
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!raw.startsWith('data:')) continue;
          try {
            const evt = JSON.parse(raw.slice(5).trim());
            if (evt.delta) {
              safeSet((m) => {
                const copy = m.slice();
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, content: (last.content || '') + evt.delta, pending: true };
                return copy;
              });
            } else if (evt.error) {
              safeSet((m) => { const copy = m.slice(); copy[copy.length - 1] = { role: 'assistant', content: '⚠️ ' + evt.error }; return copy; });
            } else if (evt.done) {
              if (evt.session_id) localStorage.setItem(SESSION_KEY, evt.session_id);
              safeSet((m) => { const copy = m.slice(); const last = copy[copy.length - 1]; copy[copy.length - 1] = { ...last, pending: false }; return copy; });
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (e) {
      const msg = /Load failed|Failed to fetch/.test(e.message)
        ? '⚠️ No pude responder ahora mismo. Escríbenos por WhatsApp mientras vuelvo.'
        : `⚠️ ${e.message}`;
      safeSet((m) => { const copy = m.slice(); copy[copy.length - 1] = { role: 'assistant', content: msg }; return copy; });
    } finally {
      if (mountedRef.current) setBusy(false);
      abortRef.current = null;
    }
  };

  // Convert the freeform "1 nombre × 2\n2 producto × 1" text into product ids by
  // querying the catalog (name contains match). We keep this best-effort: any
  // unmatched line is reported back to the user so they can rephrase.
  const submitOrder = async (form) => {
    setBusy(true);
    try {
      // Parse each non-empty line: "qty x name" OR "name x qty" OR "name" (default qty=1)
      const lines = form.items.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      const catalog = await fetch(`${API_BASE}/products`).then((r) => r.json());
      const items = [];
      const unmatched = [];
      for (const line of lines) {
        let qty = 1;
        let name = line;
        const mm = line.match(/^(\d+)\s*[x×*]\s*(.+)$/i) || line.match(/^(.+?)\s*[x×*]\s*(\d+)$/i);
        if (mm) {
          if (/^\d+$/.test(mm[1])) { qty = parseInt(mm[1], 10); name = mm[2]; }
          else { qty = parseInt(mm[2], 10); name = mm[1]; }
        }
        const n = name.trim().toLowerCase();
        const hit = catalog.find((p) => (p.name || '').toLowerCase().includes(n)) || null;
        if (hit) items.push({ product_id: hit.id, qty: Math.max(1, qty) });
        else unmatched.push(line);
      }
      if (!items.length) {
        setOrderPanel(null);
        setMessages((m) => [...m, { role: 'assistant', content: `No pude identificar ninguno de tus productos en el catálogo. Prueba con el nombre exacto que ves en la tienda:\n- ${unmatched.join('\n- ')}` }]);
        return;
      }
      const payload = {
        session_id: localStorage.getItem(SESSION_KEY) || '',
        store_id: store?.id || null,
        customer: { name: form.name, cedula: form.cedula, phone: form.phone, city: form.city, address: form.address },
        items,
        note: form.note,
      };
      const res = await fetch(`${API_BASE}/lidia/create-order`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const out = await res.json();
      // Persist customer for future visits.
      try { localStorage.setItem('bigtime_customer_data', JSON.stringify({ name: form.name, cedula: form.cedula, phone: form.phone, city: form.city, address: form.address })); } catch { /* ignore */ }
      setOrderPanel({ code: out.code, wa_link: out.store_wa_link, unmatched });
      setMessages((m) => [...m, { role: 'assistant', content: `¡Listo! Tu pedido *#${out.code}* quedó en **Pago Pendiente**. La tienda te contactará por WhatsApp en breve para cerrar. 🙏` + (unmatched.length ? `\n\n(No pude encontrar en catálogo: ${unmatched.join(', ')})` : '') }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: `⚠️ No pude registrar el pedido: ${e.message}. Intenta de nuevo o escribe por WhatsApp.` }]);
    } finally {
      setBusy(false);
    }
  };

  const isActive = config ? config.active !== false : true;
  const chips = (config?.quick_chips && config.quick_chips.length) ? config.quick_chips : DEFAULT_CHIPS;
  const brandName = config?.assistant_name || 'Lidia';

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={openChat}
          data-testid="lidia-launcher"
          aria-label={`Abrir chat con ${brandName}`}
          className="fixed z-[60] bottom-[calc(var(--fab-base)+52px)] right-3 sm:bottom-28 sm:right-6 flex items-center gap-2 bg-[#1B2A4A] hover:bg-[#141f38] text-yellow-300 rounded-full p-1 sm:pl-3 sm:pr-4 sm:py-2.5 shadow-2xl ring-2 ring-yellow-300/70 transition group"
        >
          <span className={`w-9 h-9 rounded-full bg-yellow-300 text-[#1B2A4A] flex items-center justify-center ${pulse ? 'animate-pulse' : ''}`}>
            <MessageCircle size={18} strokeWidth={2.5} />
          </span>
          <span className="text-xs font-bold uppercase tracking-wider hidden sm:block">Chat con {brandName}</span>
          {pulse && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white animate-ping" aria-hidden />}
        </button>
      )}

      {open && (
        <div
          className="fixed z-[60] bottom-[calc(var(--fab-base)+4px)] right-2 sm:bottom-24 sm:right-6 w-[min(96vw,380px)] h-[min(66vh,560px)] bg-white rounded-2xl shadow-2xl flex flex-col border border-slate-200 overflow-hidden"
          data-testid="lidia-window"
        >
          <div className="bg-gradient-to-br from-[#1B2A4A] to-[#173a5c] text-white p-3.5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-300 text-[#1B2A4A] flex items-center justify-center flex-shrink-0 shadow">
              <Sparkles size={18} strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold flex items-center gap-1.5">
                {brandName}
                <span className="text-[10px] uppercase tracking-widest text-yellow-300/90 font-semibold">BIGTIME</span>
              </div>
              <div className="text-[11px] text-white/70 leading-none mt-0.5 flex items-center gap-1">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-400' : 'bg-red-400'}`} />
                {isActive ? 'en línea · precios al mayor' : 'en pausa'}
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="lidia-close" className="p-1.5 rounded-full hover:bg-white/10" aria-label="Cerrar chat">
              <ChevronDown size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-slate-50" data-testid="lidia-messages">
            {messages.map((m, i) => (
              <div key={`msg-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`lidia-msg-${m.role}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                  m.role === 'user' ? 'bg-[#1B2A4A] text-white rounded-br-sm' : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                }`}>
                  {m.content ? formatBubble(m.content) : (
                    <span className="inline-flex gap-1 items-center text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '120ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '240ms' }} />
                    </span>
                  )}
                </div>
              </div>
            ))}
            {orderPanel && !orderPanel.code && (
              <LidiaOrderForm
                storeId={store?.id}
                busy={busy}
                onCancel={() => setOrderPanel(null)}
                onSubmit={submitOrder}
              />
            )}
            {orderPanel?.wa_link && (
              <a
                href={orderPanel.wa_link}
                target="_blank"
                rel="noreferrer"
                data-testid="lidia-store-wa"
                className="block bg-green-500 hover:bg-green-600 text-white text-xs font-bold py-2 px-3 rounded-full text-center"
              >
                📲 Avisar a la tienda por WhatsApp (#{orderPanel.code})
              </a>
            )}
          </div>

          {isActive && messages.length <= 1 && !orderPanel && (
            <div className="px-3 pt-1 pb-2 flex flex-wrap gap-1.5 bg-slate-50 border-t border-slate-100" data-testid="lidia-chips">
              {chips.map((c) => (
                <button key={c} type="button" onClick={() => send(c)}
                  className="text-[11px] bg-white border border-slate-200 hover:border-[#1B2A4A] hover:bg-yellow-50 rounded-full px-2.5 py-1 text-slate-600"
                  data-testid="lidia-chip">
                  {c}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-2.5 border-t border-slate-200 bg-white flex items-center gap-2">
            <input
              type="text" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder={isActive ? `Escríbele a ${brandName}…` : (config?.offline_message || 'En pausa temporal')}
              maxLength={400} disabled={busy || !isActive}
              data-testid="lidia-input"
              className="flex-1 px-3 py-2 text-sm rounded-full border border-slate-200 focus:border-[#1B2A4A] focus:outline-none disabled:bg-slate-100"
            />
            <button type="submit" disabled={busy || !isActive || !input.trim()} data-testid="lidia-send"
              className="w-9 h-9 rounded-full bg-[#1B2A4A] text-yellow-300 disabled:opacity-40 flex items-center justify-center hover:bg-[#141f38] transition"
              aria-label="Enviar">
              <Send size={15} />
            </button>
          </form>
          <p className="text-[10px] text-slate-400 text-center pb-1.5 bg-white">
            {isActive ? 'Respuestas orientativas. Para pedidos, escribe **CONFIRMAR PEDIDO**.' : (config?.offline_message || 'Chat en pausa temporal')}
          </p>
        </div>
      )}
    </>
  );
}
